// deps
import React, { useCallback, useMemo, useRef } from "react";

// internal-deps
import { TDSuccessCardType, TradeDiscountSuccessCard, GenericTDSuccessCardType } from "@swiggy-private/cart-types";
import { DialogHOC, extractImageConfigFromString, format, isEmpty, useEffectOnce } from "@swiggy-private/core-ui";

// constants
import { TEXTS } from "@cart-ui/constants";
import { DELIGHT_POPUP_TEST_IDS } from "@swiggy-private/cart-test-ids";

// styles
import {
    Container,
    ContentContainer,
    CTAContainer,
    SavingsText,
    StyledGenericIcon,
    SubText,
    UnderUtilisationCallout,
    TimeContainer,
    Title,
    ImageContainer,
} from "./styles";

// components
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";
import { default as ClearLightIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/clearLight/24";
import { SuperButton } from "@swiggy-private/dls-web-core/dist/components/SuperButton";
import { TextButton } from "@swiggy-private/dls-web-core/dist/components/TextButton";

// helpers
import { getUnderUtilisationText } from "@cart-ui/helpers/Coupon";
import { getSubTextVariantAndColor } from "@cart-ui/helpers/DelightPopup";

export interface DelightPopupProps {
    data: TradeDiscountSuccessCard;
    closeModal: VoidFunction;
    onImpressionDelightPopup: (isPHCallout?: boolean, isDealRush?: boolean, cardType?: string) => void;
    onClickCloseDelightPopup: (isDealRush?: boolean, cardType?: string) => void;
    isToingCart?: boolean;
    isJioHotstarFlow?: boolean;
}

const DelightPopup: React.FC<DelightPopupProps> = ({
    data,
    closeModal,
    onImpressionDelightPopup,
    onClickCloseDelightPopup,
    isToingCart,
    isJioHotstarFlow,
}: DelightPopupProps) => {
    const animationRef = useRef(null);
    const isPHCallout = data.td_success_card_type === TDSuccessCardType.OFFER_CARD_TYPE_POCKET_HERO;
    const isDealRush = data.td_success_card_type === TDSuccessCardType.OFFER_CARD_TYPE_DEAL_RUSH;
    const isGenericDelightPopup =
        !!data.td_success_card_type && GenericTDSuccessCardType.includes(data.td_success_card_type);
    const { width, height, cloudinaryId } = useMemo(() => {
        if (isEmpty(data.logo_id)) return { width: undefined, height: undefined, cloudinaryId: undefined };
        return (
            extractImageConfigFromString(data.logo_id) || {
                width: undefined,
                height: undefined,
                cloudinaryId: data.logo_id,
            }
        );
    }, [data.logo_id]);
    const delightSavingsText = isPHCallout ? data.savings : format(TEXTS.INSTANT_OFF, data.savings);
    const underUtilisationText = getUnderUtilisationText(data.extra_savings_callout);
    const popupCtaColor = data.cta?.color;

    useEffectOnce(() => {
        if (data?.is_reward_offer || isPHCallout || isDealRush || isGenericDelightPopup) {
            onImpressionDelightPopup(isPHCallout, isDealRush, data?.td_success_card_type);
        }
    });

    // manual closing
    const onClosePopup = useCallback(() => {
        onClickCloseDelightPopup(isDealRush, data?.td_success_card_type);
        closeModal();
    }, [onClickCloseDelightPopup, closeModal, isDealRush, data?.td_success_card_type]);

    const popupSubtitle = useMemo(() => format(data.subtitle, data.savings), [data.savings, data.subtitle]);

    return (
        <Container aria-modal="true" data-testid={DELIGHT_POPUP_TEST_IDS.POPUP} isDealRush={isDealRush} isToingCart={isToingCart}>
            <ContentContainer
                as="div"
                isPHCallout={isPHCallout}
                isDealRush={isDealRush}
                isGenericDelightPopup={isGenericDelightPopup}
                isJioHotstarFlow={isJioHotstarFlow}
            >
                {!isPHCallout && !isDealRush && !isGenericDelightPopup && (
                    <>
                        {!isEmpty(data.logo_id) && (
                            <Image cloudinaryId={data.logo_id} lazyLoad={false} width={isJioHotstarFlow ? "66px" : "90px"} />
                        )}
                        <Title variant="V2.body2.neutral.medium" color="text_Med_Emphasis" isJioHotstarFlow={isJioHotstarFlow}>
                            Congratulations!
                        </Title>
                        <SavingsText variant="V2.heading1.neutral.black" color="text_Highest_Emphasis" isJioHotstarFlow={isJioHotstarFlow}>
                            {delightSavingsText}
                        </SavingsText>
                        <SubText
                            {...getSubTextVariantAndColor(isPHCallout, isJioHotstarFlow)}
                            isPHCallout={isPHCallout}
                        >
                            {data.subtitle}
                        </SubText>
                    </>
                )}
                {isPHCallout && (
                    <>
                        {!isEmpty(data.logo_id) && <Image cloudinaryId={data.logo_id} lazyLoad={false} width="146px" />}
                        <SavingsText variant="V2.heading1.neutral.black" color="text_Highest_Emphasis">
                            {delightSavingsText}
                        </SavingsText>
                        <SubText
                            {...getSubTextVariantAndColor(isPHCallout, isJioHotstarFlow)}
                            isPHCallout={isPHCallout}
                        >
                            {data.subtitle}
                        </SubText>
                    </>
                )}
                {isGenericDelightPopup && (
                    <>
                        {!isEmpty(data.logo_id) && !isEmpty(width) && !isEmpty(height) && (
                            <ImageContainer>
                                <Image cloudinaryId={cloudinaryId} width={width} height={height} />
                            </ImageContainer>
                        )}
                        {data.title && (
                            <Title variant="V2.body2.neutral.medium" color="text_Med_Emphasis">
                                {data.title}
                            </Title>
                        )}
                        <SavingsText
                            variant="V2.heading1.neutral.black"
                            color="text_Highest_Emphasis"
                            isGenericDelightPopup={isGenericDelightPopup}
                            dangerouslySetInnerHTML={{ __html: popupSubtitle }}
                        />
                    </>
                )}
                {isDealRush && (
                    <>
                        {!isEmpty(data.logo_id) && <Image cloudinaryId={data.logo_id} lazyLoad={true} width="68px" />}
                        {data.title && (
                            <Title variant="V2.body2.neutral.medium" color="text_Med_Emphasis">
                                {data.title}
                            </Title>
                        )}
                        <SavingsText
                            variant="V2.heading1.neutral.bold"
                            color="text_Highest_Emphasis"
                            isDealRush={isDealRush}
                            dangerouslySetInnerHTML={{ __html: popupSubtitle }}
                        />
                    </>
                )}
                {underUtilisationText && !isDealRush && !isGenericDelightPopup && (
                    <UnderUtilisationCallout
                        variant="V2.body2.neutral.regular"
                        color="text_Med_Emphasis"
                        dangerouslySetInnerHTML={{ __html: underUtilisationText }}
                        isJioHotstarFlow={isJioHotstarFlow}
                    />
                )}
                <StyledGenericIcon Icon={ClearLightIcon} onClick={onClosePopup} />
            </ContentContainer>
            <CTAContainer isSmallMargin={isGenericDelightPopup || isDealRush} isJioHotstarFlow={isJioHotstarFlow}>
                {(isGenericDelightPopup || isDealRush) && data.cta?.text ? (
                    <TextButton
                        data-testid={DELIGHT_POPUP_TEST_IDS.CTA_BUTTON}
                        buttonSize="medium"
                        onClick={onClosePopup}
                        color={popupCtaColor || (isGenericDelightPopup ? "primary" : "rain")}
                    >
                        {data.cta.text}
                    </TextButton>
                ) : (
                    <SuperButton
                        data-testid={DELIGHT_POPUP_TEST_IDS.CTA_BUTTON}
                        variant="fullwidth"
                        buttonType="primary"
                        buttonSize={isJioHotstarFlow ? "medium" : "large"}
                        onClick={onClosePopup}
                    >
                        {TEXTS.COUPON_SUCCESS_CTA_TEXT}
                    </SuperButton>
                )}
            </CTAContainer>

            <TimeContainer ref={animationRef} onAnimationEnd={closeModal} ctaColor={popupCtaColor} />
        </Container>
    );
};

export default DialogHOC(React.memo(DelightPopup));
